import { fetchWithRetry } from '@flos/react-ui';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { defaultQueue } from '../const';
import { isScheduledBooking } from '../services/callback-whitelist';
import getDestinationCode from '../services/destination-code';
import { buildQueueDescription } from '../services/queue-description';
import { IState, KnownError, KnownErrorType } from '../types';
import { callStartTime, DATE_ISO_FORMAT, getIsoDateFromDanishDate } from '../utils/date-utils';
import log from '../utils/logger';
import { NavigationState } from './navigation.type';

export const submit = createAsyncThunk<void, void, { state: IState; rejectValue: KnownErrorType }>(
  'navigation/submit',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();

    const {
      navigation,
      settings: {
        appName,
        endpointConfig: { apiAwsBase: host },
        siteConfig: { environment },
      },
    } = state;

    try {
      if (isScheduledBooking(navigation)) {
        await fetchWithRetry(
          `${host}/ninjas-schedule-a-callback-api-v2/v1/callback-availability?callbackTime=${format(
            callStartTime(navigation.bookingDate!, navigation.bookingTime!),
            DATE_ISO_FORMAT
          )}`,
          {
            method: 'POST',
            headers: {
              'x-top-appname': appName,
            },
          }
        );
      }
    } catch (e) {
      const err = e as any;

      const body = await err?.response?.json?.();

      console.log(`body`, body);

      if (err?.response?.status === 400 && KnownError.guard(body)) {
        return thunkAPI.rejectWithValue(body.error);
      } else {
        throw e;
      }
    }

    // Post to Genesys

    const payload = createSubmissionPayload(navigation);
    log.info('Submitting to genesys', payload);

    const response = await fetchWithRetry(`${host}/register-callback-api/v1/submit`, {
      method: 'POST',
      headers: {
        'x-top-appname': appName,
        'x-top-env': environment,
      },
      body: JSON.stringify(payload),
    });

    if (Math.floor(response.status / 100) !== 2) {
      throw new Error(`Status: ${response.status}, message: ${await response.text()}`);
    }

    log.info('Genesys OK', `Status: ${response.status}, body: ${await response.text()}`);

    // Register load
    registerLoad(state);

    // Send SMS
    sendTextMessage(state);
  }
);

const sendTextMessage = async (state: IState) => {
  const {
    navigation,
    settings: {
      appName,
      endpointConfig: { apiAwsBase: host },
      siteConfig: { environment },
    },
  } = state;

  try {
    const resp = await fetchWithRetry(
      `${host}/ninjas-schedule-a-callback-api-v2/v1/submission${createSmsPayload(navigation)}`,
      {
        method: 'POST',
        headers: {
          'x-top-appname': appName,
          'x-top-env': environment,
          'x-aws-env': extractAwsEnvironment(host),
        },
      }
    );

    if (Math.floor(resp.status / 100) !== 2) {
      throw new Error(`Status: ${resp.status}, message: ${await resp.text()}`);
    }

    log.info(
      'ninjas-schedule-a-callback-api-v2',
      resp.status,
      resp.statusText,
      Array.from(resp.headers),
      await resp.text()
    );
  } catch (e) {
    log.error('Unable to send SMS: ', e);
    // Intentionally not to re-throw error as Genesys has received the request.
  }
};

const registerLoad = async (state: IState) => {
  const {
    navigation,
    settings: {
      appName,
      endpointConfig: { apiAwsBase: host },
    },
  } = state;

  if (isScheduledBooking(navigation)) {
    try {
      const resp = await fetchWithRetry(`${host}/ninjas-schedule-a-callback-api-v2/v1/schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-top-appname': appName,
        },
        body: JSON.stringify(createBookingLoadPayload(navigation)),
      });

      if (Math.floor(resp.status / 100) !== 2) {
        throw new Error(`Status: ${resp.status}, message: ${await resp.text()}`);
      }

      log.info('Register scheduling load', resp.status, resp.statusText, Array.from(resp.headers), await resp.text());
    } catch (e) {
      log.error('Unable to register load: ', e);
      // Intentionally not to re-throw error as Genesys has received the request.
    }
  }
};

const createSubmissionPayload = (state: NavigationState): {} => ({
  phone: state.mobile,
  ...getDestinationCode(state),
  queueDescription: buildQueueDescription(state),
  ...(isScheduledBooking(state)
    ? {
        callbackStartTime: format(callStartTime(state.bookingDate!, state.bookingTime!), DATE_ISO_FORMAT),
        callbackEndTime: format(callStartTime(state.bookingDate!, state.bookingTime!), DATE_ISO_FORMAT),
        callbackType: '5 Minutter før',
      }
    : {
        callStartTime: '',
      }),
});

const createSmsPayload = (state: NavigationState): string => {
  const { mobile, bookingDate, bookingTime } = state;
  if (isScheduledBooking(state)) {
    return `?type=scheduled&mobile=${mobile}&date=${encodeURIComponent(
      getIsoDateFromDanishDate(bookingDate!)
    )}&time=${encodeURIComponent(bookingTime!.padStart(5, '0'))}`;
  } else {
    return `?type=asap&mobile=${mobile}`;
  }
};

const createBookingLoadPayload = ({ bookingDate, bookingTime }: NavigationState) => ({
  department: defaultQueue,
  time: bookingTime!.padStart(5, '0'),
  day: getIsoDateFromDanishDate(bookingDate!),
});

// expose for testing
export function extractAwsEnvironment(awsBase: string): string {
  const regex = /^https?:\/\/api\.([^.]+)\.topdanmark\.cloud\/?$/i.exec(awsBase);

  if (regex) {
    return regex[1];
  } else {
    return awsBase;
  }
}
